import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { authApi } from "../api/auth";
import type { TokenVerifyResult } from "../api/auth";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";

const TOKEN_KEY = "acts_token";

const schema = z
  .object({
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlToken = searchParams.get("token") ?? "";

  const [tokenState, setTokenState] = useState<"checking" | "valid" | "invalid">("checking");
  const [tokenInfo, setTokenInfo] = useState<TokenVerifyResult | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!urlToken) {
      setTokenState("invalid");
      return;
    }
    authApi
      .verifyToken(urlToken, "invite")
      .then((res) => {
        if (res.valid) {
          setTokenInfo(res);
          setTokenState("valid");
        } else {
          setTokenState("invalid");
        }
      })
      .catch(() => setTokenState("invalid"));
  }, [urlToken]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const tokens = await authApi.acceptInvite(urlToken, data.newPassword);
      // Store session and navigate — AuthProvider will pick it up
      localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
      toast.success("Welcome to Camp ACTS!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to accept invitation. The link may have expired."
      );
    } finally {
      setLoading(false);
    }
  };

  if (tokenState === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (tokenState === "invalid") {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-stone-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-stone-200 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-display font-bold text-stone-900 mb-2">Invitation expired</h2>
          <p className="text-stone-500 text-sm mb-6">
            This invitation link is invalid or has expired. Please contact your camp administrator.
          </p>
          <Link to="/login" className="text-sm text-forest-600 hover:underline font-medium">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-stone-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
        <img src="/ch-logo-green.png" alt="Camp Highlands" className="h-12 w-auto mb-8 mx-auto" />

        <h1 className="text-2xl font-display font-bold text-stone-900 mb-1">
          Welcome{tokenInfo?.firstName ? `, ${tokenInfo.firstName}` : ""}!
        </h1>
        <p className="text-stone-500 text-sm mb-6">
          You&apos;ve been invited to Camp ACTS. Set a password to get started.
          {tokenInfo?.email && (
            <span className="block mt-1 font-medium text-stone-700">{tokenInfo.email}</span>
          )}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            hint="At least 8 characters"
            error={errors.newPassword?.message}
            {...register("newPassword")}
          />

          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword")}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            className="w-full mt-2"
          >
            Set password &amp; sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
